import { z } from 'zod';

import type { HolidayInputs } from './api';

// Keep the supported countries in one place so the browser and form stay aligned.
export const holidayCountries = [
  { value: 'CA', label: 'Canada' },
  { value: 'US', label: 'United States' },
];

const feedSchema = z.array(
  z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    name: z.string(),
    localName: z.string(),
    countryCode: z.string(),
    global: z.boolean(),
    counties: z.array(z.string()).nullable(),
  }),
);

export interface PublicHoliday {
  date: string;
  name: string;
  localName: string;
  country: string;
  states: string[];
}

export async function fetchPublicHolidays(
  country: string,
  year: number,
): Promise<PublicHoliday[]> {
  if (
    !holidayCountries.some(({ value }) => value === country) ||
    !Number.isInteger(year) ||
    year < 1900 ||
    year > 2100
  )
    throw Error('Choose a supported country and a year between 1900 and 2100.');

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    // Public feed: never send dashboard credentials to this external service.
    const response = await fetch(
      `https://date.nager.at/api/v3/PublicHolidays/${year}/${country}`,
      { signal: controller.signal, credentials: 'omit' },
    );
    if (!response.ok) throw Error('Holiday feed unavailable');
    return feedSchema
      .parse(await response.json())
      .filter(
        (holiday) =>
          holiday.countryCode === country &&
          holiday.date.startsWith(`${year}-`),
      )
      .flatMap((holiday) => {
        const states = holiday.global
          ? []
          : [
              ...new Set(
                (holiday.counties ?? [])
                  .filter((county) => county.startsWith(`${country}-`))
                  .map((county) => county.slice(country.length + 1)),
              ),
            ].sort();
        // A regional holiday with missing scope must never select the whole country.
        if (!holiday.global && !states.length) return [];
        return [
          {
            date: holiday.date,
            name: holiday.name,
            localName: holiday.localName,
            country,
            states,
          },
        ];
      })
      .sort(
        (a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name),
      );
  } catch {
    throw Error(
      'Could not load public holidays. Try again or enter a date manually.',
    );
  } finally {
    clearTimeout(timeout);
  }
}

export function selectHoliday(
  inputs: HolidayInputs,
  holiday: PublicHoliday,
): HolidayInputs {
  return {
    ...inputs,
    holiday_date: holiday.date,
    country: holiday.country,
    states: holiday.states.join(','),
    // The closure API supports named Thanksgiving, otherwise use a generic day off.
    code: /\bthanksgiving\b/i.test(holiday.name) ? '#-THANKSGIVING' : 'X',
    school_ids: inputs.country === holiday.country ? inputs.school_ids : '',
  };
}
